(ns io.daskul.webapp.widgets.form.sections
  "Form sections with placement support (F-056 :ui :form :sections).

   Splits sections by :placement (:main or :sidebar) and renders grouped
   form fields with section titles. Row layout is explicit: a nested vector
   in :fields renders as one simple-grid row (single column on mobile), a
   bare keyword renders full-width. Pure row logic lives in section-rows.

   sectioned-form-body is the F-057 primitive: the whole sectioned form
   body as one data-driven component, shared by the generic edit page and
   callable from custom pages."
  (:require
   [uix.core :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.widgets.form.field :as ff]
   [io.daskul.webapp.widgets.form.section-nav :as nav]
   [io.daskul.webapp.widgets.form.section-rows :as rows]))

(defn- editable-form-field
  "Standard editable form field wired to the shared form ctx
   ({:agg-key :form-data :set-field! :read-only? :auto-filled-keys
   :handle-auto-fill})."
  [{:keys [agg-key form-data set-field! read-only?
           auto-filled-keys handle-auto-fill]}
   field]
  (let [field-key (:name field)]
    ($ ff/form-field
      {:key (name field-key)
       :agg-key agg-key
       :field field
       :value (get form-data field-key)
       :on-change #(set-field! field-key %)
       ;; A widget that owns two columns (value + its unit) needs to write a
       ;; sibling; :on-change is bound to this field's key alone.
       :set-form-field! set-field!
       :disabled (or read-only? (:readonly field)
                     (contains? auto-filled-keys field-key))
       :form-data form-data
       :on-auto-fill (when (get-in field [:ui :auto-fill])
                       handle-auto-fill)})))

(defui section-fields
  "Render a section's form fields, with its title in a gutter column beside
   them (F-060).

   The title sits OUTSIDE the field column, and that is the whole point: it is
   what makes it unmistakable for a field label. Before this, the title
   rendered directly above the fields in the same column at 14px/600 against
   labels at 14px/500 — the same size, one weight step apart — so a section
   title read as another label and you could not see where a section began.
   Hierarchy here is positional, not typographic, which is also why the
   compact scale stays legible.

   `gutter?` false renders the fields full width with no title: a form with
   one section has nothing to disambiguate, and the gutter would cost ~174px
   of form width for decoration. See `sectioned-form-body`.

   Every number comes from a token. The phone collapse (single column, title
   stacked above its fields) is `breakpoints.css` flipping --section-grid and
   --section-title-position; no media query belongs in here.

   When the section has :readonly true, renders values as plain text."
  [{:keys [section fields form-data gutter? last?] :as ctx}]
  (let [resolved-rows (rows/resolve-rows section fields)
        section-readonly? (:readonly section)
        render-field
        (fn [field]
          (let [field-key (:name field)]
            (if section-readonly?
              ;; Read-only: render as label + value text pair
              (let [label (or (get-in field [:ui :label]) (name field-key))
                    value (get form-data field-key)]
                ($ ui/stack {:key (name field-key) :gap 2}
                  ($ ui/text {:size "sm" :c "dimmed"} label)
                  ($ ui/text {:size "sm"} (or (some-> value str) "—"))))
              ;; Editable: render form field
              (editable-form-field ctx field))))
        ;; Plain flex rather than ui/stack: Mantine's `gap` prop runs through
        ;; getSpacing, which would turn "var(--row-gap)" into
        ;; var(--mantine-spacing-var(--row-gap)). Tokens have to be set as CSS.
        field-column
        ($ :div {:style {:display "flex" :flexDirection "column"
                         :gap "var(--row-gap)"}}
          (for [[i row] (map-indexed vector resolved-rows)]
            (if (= 1 (count row))
              ;; Full-width row
              (render-field (first row))
              ;; Explicit multi-column row — collapses to one column on mobile.
              ;; Mantine's responsive `cols` handles n columns; --field-row-columns
              ;; only describes a pair, so it is left for the FieldRow primitive.
              ;;
              ;; Everything else about the row's geometry — both gaps, and the
              ;; four shared tracks that keep a field with a :description from
              ;; pushing its input below its neighbour's (#770) — is
              ;; css/components/form-row.css, keyed off this class. It has to be
              ;; a stylesheet: it places Mantine's own label / description /
              ;; input / error elements, which no prop reaches.
              ;;
              ;; That is also why `spacing` is gone rather than pointed at
              ;; --col-gap: it runs through getSpacing, so a var() here would
              ;; expand to var(--mantine-spacing-var(--col-gap)).
              ($ ui/simple-grid {:key (str "row-" i)
                                 :className "fw-form-row"
                                 :cols #js {:base 1 :sm (count row)}}
                (for [field row]
                  (render-field field))))))]
    (when (seq resolved-rows)
      (if-not gutter?
        field-column
        ($ :div {;; The chip rail's jump target (#704). scroll-margin-top is
                 ;; what keeps the heading from parking under the things pinned
                 ;; above it, so scrollIntoView AND a plain `#form-section-x`
                 ;; deep link both land correctly with no JS arithmetic.
                 ;;
                 ;; Two terms, because two things are pinned. --anchor-offset
                 ;; is the handover's number for the rail alone (61px on a
                 ;; phone, 16px on desktop where there is no rail); this app
                 ;; also has a fixed AppShell header, which that number knows
                 ;; nothing about. Adding it here rather than folding it into
                 ;; the token keeps --app-shell-header-offset resolving on an
                 ;; element inside the shell, where Mantine actually sets it —
                 ;; in :root it would fall back to its default and stop
                 ;; tracking the impersonation banner.
                 :id (nav/anchor-id (:id section))
                 :style {:display "grid"
                         :scrollMarginTop (str "calc(var(--app-shell-header-offset, 60px)"
                                               " + var(--anchor-offset, 0px))")
                         :gridTemplateColumns "var(--section-grid)"
                         :gap "var(--gutter-gap)"
                         :padding "var(--section-pad) 0"
                         ;; A hairline closes each section; the last one has
                         ;; the form's own edge below it already.
                         :borderBottom (when-not last?
                                         "1px solid var(--border-subtle)")}}
          ($ :div
            ($ :div {:style {:position "var(--section-title-position)"
                             :top "16px"
                             :fontFamily "var(--font-heading)"
                             :fontSize "var(--section-title-size)"
                             :fontWeight "var(--section-title-weight)"
                             :letterSpacing "var(--section-title-tracking)"
                             :color "var(--text)"}}
              (:title section))
            ;; Hidden at compact density — --section-desc is `none` there. The
            ;; most "marketing page" element in the system, so it is off by
            ;; default rather than opt-out.
            (when-let [d (:description section)]
              ($ :div {:style {:display "var(--section-desc)"
                               :fontSize "var(--helper-size)"
                               :lineHeight 1.5
                               :color "var(--text-faint)"
                               :marginTop "4px"}}
                d)))
          field-column)))))

(defui sectioned-form-body
  "The sectioned form body as a data-driven primitive (F-057).

   Renders :main-placement sections as titled groups with explicit rows,
   then any schema fields not assigned to a section, full-width. Falls
   back to a flat field list when no :main sections are declared. Takes
   :sections + :fields plus the shared form ctx (see editable-form-field),
   so custom pages compose the exact body the generic edit page renders."
  [{:keys [sections fields] :as ctx}]
  (let [{:keys [main]} (rows/split-sections sections)
        loose (rows/unsectioned-fields sections fields)
        ;; Sectioning is a DISAMBIGUATION device (F-060). One section has
        ;; nothing to disambiguate, so it renders full width with no gutter
        ;; and no title, rather than spending ~174px of form width on
        ;; decoration. `student` is the case that motivated it: five editable
        ;; fields in a single section.
        ;;
        ;; Loose fields count. With one section plus unsectioned fields there
        ;; ARE two groups on the page, and dropping the title would leave the
        ;; reader unable to tell which fields the section covers.
        gutter? (or (> (count main) 1) (and (seq main) (seq loose)))
        last-idx (dec (count main))]
    (if (seq main)
      ($ :<>
        (for [[i section] (map-indexed vector main)]
          ($ section-fields
            (assoc ctx :key (name (:id section)) :section section
                   :gutter? gutter?
                   ;; Only truly last when nothing follows it.
                   :last? (and (= i last-idx) (empty? loose)))))
        ;; Fields not assigned to any section
        (for [field loose]
          (editable-form-field ctx field)))
      ($ :<>
        (for [field fields]
          (editable-form-field ctx field))))))
